import React, { useState, useEffect, useRef } from 'react';
import { captureException, captureMessage } from "@sentry/react";
import API from '../../../API';
import countriesDa from '../../countries/CountriesDa';
import countriesDe from '../../countries/CountriesDe';
import countriesEn from '../../countries/CountriesEn';
import countriesSe from '../../countries/CountriesSe';
import { Container, Form } from 'reactstrap';
import { T } from '../../../traks';
import { connect } from 'react-redux';
import { PaymentSpinner } from '../Payment/PaymentSpinner';
import { PaymentMethod } from './PaymentMethod';
import { UserDetailsForm } from './UserDetailsForm';
import { AddressForm } from './AddressForm';
import { TermsAndConditions } from './TermsAndConditions';
import { validateFields, createOrderObject, createPaymentObject, requestOrderId, createPayment } from './CheckoutUtils';
import { checkUserAge } from '../../../misc';
import { site_name } from '../../../Site';
import { NotLoggedIn } from './NotLoggedIn';
import { CompanyInfo } from './CompanyInfo';
import OrderFooter from '../Globals/OrderFooter';

export const ConnectedCheckout = connect((state) => ({ me: state.me }))((props) => <Checkout {...props} />);

const Checkout = (props) => {
  const { setOrderId, defaultCurrency, setOrderDetails, cart, setView, me, vatRate, game, onComplete } = props;

  const [formValues, setFormValues] = useState({
    name: '',
    company: '',
    email: '',
    phoneNumber: '',
    country: '',
    postalCode: '',
    streetAddress: '',
    city: '',
    selectedPaymentMethod: 'credit_card',
    cvr: '',
    ean: '',
  });
  const [errors, setErrors] = useState({});
  const [countryList, setCountryList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isInfoLoading, setIsInfoLoading] = useState(false);
  const [ageError, setAgeError] = useState('');
  const [hasAcceptedTradeConditions, setHasAcceptedTradeConditions] = useState(false);
  
  const isUserLoggedIn = !!me;
  
  // Generic updater for form fields
  const updateField = (field, value) => {
    setFormValues((prev) => ({ ...prev, [field]: value }));
  };
  
  useEffect(() => {
    if (IS_AKTIEDYSTEN_DK) setCountryList(countriesDa);
    else if (IS_AKTIEKAMPEN_SE) setCountryList(countriesSe);
    else if (IS_TRADERR_COM) setCountryList(countriesEn);
    else if (IS_INVESTSPIEL_DE) setCountryList(countriesDe);
  }, []);
  
  useEffect(() => {
    setIsInfoLoading(true);
    API.get('/a/prev_order_info', {}, (response, status) => {
      if (status === 200) {
        updateField('phoneNumber', response.Phone);
        updateField('country', response.Country);
        updateField('postalCode', response.Postalcode);
        updateField('city', response.City);
        updateField('streetAddress', response.StreetAddress);
        updateField('company', response.Company.String);
      }

        if (response && response.error) {
            captureMessage("Previous order info error", {
                level: "error",
                extra: {
                response: response,
                status: status
                }
            });
        }

      setIsInfoLoading(false);
    });
  }, [me]);
  
  useEffect(() => {
    if (me) {
      updateField('name', me.ScreenName || '');
      updateField('email', me.LocalEmail || '');
    }
  }, [me]);

  const isMounted = useRef(true);
  useEffect(() => {
    isMounted.current = true;
    return () => { isMounted.current = false; };
  }, []);
    
  const acceptTermsAndConditions = (checked) => {
    const userAge = checkUserAge(new Date(me.Birthdate));
    if (userAge >= 16) {
      setHasAcceptedTradeConditions(checked);
    } else {
      setAgeError(`* You are ${userAge} years old, you must be at least 16 to order.`);
      setHasAcceptedTradeConditions(false);
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
  
    const newErrors = validateFields({ ...formValues, hasAcceptedTradeConditions });
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
  
    setErrors({});
    setIsLoading(true);
  
    try {
      const orderObject = createOrderObject(defaultCurrency, vatRate, cart);
      const orderId = await requestOrderId(orderObject);
  
      if (!isMounted.current) return;
      setOrderId(orderId);
  
      const paymentObject = createPaymentObject(orderId.replace(/"/g, ''), formValues, cart, vatRate);
      const paymentId = await createPayment(paymentObject, game ? game : 0);
  
      if (!isMounted.current) return;
      const combinedDetails = {
        paymentId,
        order: orderObject,
        payment: paymentObject,
      };
      setOrderDetails(combinedDetails);
  
      if (!isMounted.current) return;
      if (formValues.selectedPaymentMethod === 'credit_card') {
        setView('payment');
      } else if (formValues.selectedPaymentMethod === 'ean') {
        onComplete({orderId: orderId, data: {message: 'completed'}});
      } else {
        setView('error');
      }
  
    } catch (error) {
      captureException(error);
      if (isMounted.current) setView('error');
    } finally {
      if (isMounted.current) setIsLoading(false);
    }
  };
  
  return (
    <div className="checkout-container">
      {isInfoLoading || isLoading ? (
        <PaymentSpinner />
      ) : isUserLoggedIn ? (
        <Container className="checkout-form-container">
          <Form id="checkoutForm" className="checkout-form" onSubmit={handleSubmit}>
            <UserDetailsForm
              updateField={updateField}
              values={formValues}
              errors={errors}
              isLoading={isLoading}
            />
            <AddressForm
              updateField={updateField}
              values={formValues}
              errors={errors}
              countryList={countryList}
              isLoading={isLoading}
            />
            <PaymentMethod updateField={updateField} />
            {formValues.selectedPaymentMethod === 'ean' && (
              <CompanyInfo 
                updateField = {updateField}
                values={formValues}
                errors={errors}
                isLoading={isLoading} />
            )}
            <TermsAndConditions
              hasAcceptedTradeConditions={hasAcceptedTradeConditions}
              acceptTermsAndConditions={acceptTermsAndConditions}
              errors={errors}
              ageError={ageError}
              siteName={site_name}
            />
          </Form>
        </Container>
      ) : (
        <NotLoggedIn />
      )}

      {isUserLoggedIn && (
        <OrderFooter
            total={cart.lines[0].discountedTotal}
            totalNoVat={cart.lines[0].discountedTotalNoVat}
            onBack={() => setView('cart')}
            formId="checkoutForm"
            nextLabel={<T>Payment</T>}
            disableNext={isLoading}
        />
      )}
    </div>
  );
};

export default Checkout;



// WEBPACK FOOTER //
// ./src/c/PremiumKeys/Checkout/Checkout.js